import { createContext, useContext, useState, useCallback } from 'react';

const TabsContext = createContext<{
  value: string;
  onValueChange: (val: string) => void;
} | null>(null);

function useTabsContext() {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('Tabs compound components must be used inside Tabs');
  return ctx;
}

/* ── Root ── */
export function Tabs({
  defaultValue,
  value,
  onValueChange,
  children,
  className = '',
}: {
  defaultValue?: string;
  value?: string;
  onValueChange?: (val: string) => void;
  children: React.ReactNode;
  className?: string;
}) {
  const [internal, setInternal] = useState(defaultValue ?? '');
  const active = value !== undefined ? value : internal;
  const setter = useCallback(
    (val: string) => {
      if (onValueChange) onValueChange(val);
      setInternal(val);
    },
    [onValueChange]
  );

  return (
    <TabsContext.Provider value={{ value: active, onValueChange: setter }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

/* ── List ── */
export function TabsList({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      className={`inline-flex items-center rounded-lg border border-border bg-muted p-1 ${className}`}
    >
      {children}
    </div>
  );
}

/* ── Trigger ── */
export function TabsTrigger({
  value,
  children,
  className = '',
}: {
  value: string;
  children: React.ReactNode;
  className?: string;
}) {
  const ctx = useTabsContext();
  const active = ctx.value === value;

  return (
    <button
      role="tab"
      aria-selected={active}
      data-state={active ? 'active' : 'inactive'}
      onClick={() => ctx.onValueChange(value)}
      className={`
        relative inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium transition-all
        data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm
        data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
        disabled:pointer-events-none disabled:opacity-50
        ${className}
      `}
    >
      {children}
    </button>
  );
}

/* ── Content ── */
export function TabsContent({
  value,
  children,
  className = '',
}: {
  value: string;
  children: React.ReactNode;
  className?: string;
}) {
  const ctx = useTabsContext();
  if (ctx.value !== value) return null;

  return (
    <div
      role="tabpanel"
      data-state="active"
      className={`mt-3 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${className}`}
    >
      {children}
    </div>
  );
}

/* ── Re-export with same names as shadcn ── */
export { Tabs as TabsRoot };
